import { useEffect, useState, useCallback, useRef } from 'react'

/**
 * Wraps `setInterval`. Triggers the function each interval.
 * @param {number} timeout in millesecond to countdown
 * @param {number} interval in milliseconds
 * @param {boolean} runOnLoad by default
 * @return {array} of time<number>, start<function>, stop<function>, reset<function>
 */
function useTimer(timeout, interval = 1000, runOnLoad = true) {
  const [time, setTime] = useState(timeout)
  const currentTime = useRef()
  const intervalRef = useRef()
  const clearRef = useRef()
  const timerIsRunning = useRef()

  const start = useCallback(
    _timeout => {
      if (!timerIsRunning.current) {
        timerIsRunning.current = true
        if (_timeout !== undefined) {
          currentTime.current = _timeout
        }
        setTime(currentTime.current)

        intervalRef.current = setInterval(() => {
          currentTime.current = currentTime.current - interval
          if (currentTime.current <= 0) {
            currentTime.current = 0
            clearRef.current()
          }
          setTime(currentTime.current)
        }, interval)
        clearRef.current = () => {
          timerIsRunning.current = false
          clearInterval(intervalRef.current)
        }
      }
    },
    [interval],
  )

  const pause = () => {
    if (timerIsRunning.current) {
      clearRef.current()
    }
  }

  const reset = () => {
    if (timerIsRunning.current) {
      pause()
      timerIsRunning.current = false
      start(timeout)
    } else {
      setTime(timeout)
      currentTime.current = timeout
    }
  }

  useEffect(() => {
    if (runOnLoad) {
      timerIsRunning.current = false
      start(timeout)
    }
    currentTime.current = timeout
    return () => {
      if (timerIsRunning.current) {
        clearRef.current()
      }
    }
  }, [timeout, interval, runOnLoad, start])

  return [time, start, pause, reset]
}

export { useTimer }
